<template>
  <section class="export-email-form">
    <div class="export-email-form__header">
      <h3 class="export-email-form__title">{{ t("email_section_title") }}</h3>
      <p class="export-email-form__intro">{{ t("email_section_intro") }}</p>
    </div>

    <label class="export-email-form__field">
      <span class="export-email-form__label">{{ t("preparer_label") }}</span>
      <sp-text-field
        v-model="preparerName"
        :aria-label="t('preparer_label')"
        :input-test-id="TestId.directoryPdf.preparerName"
        autocomplete="name"
        :disabled="sending"
      ></sp-text-field>
      <span class="export-email-form__hint">{{ t("preparer_hint") }}</span>
      <span v-if="emailFieldErrors.preparer_name" role="alert" class="export-email-form__error">
        {{ emailFieldErrors.preparer_name }}
      </span>
    </label>

    <label class="export-email-form__field">
      <span class="export-email-form__label">{{ t("emails_label") }}</span>
      <sp-text-field
        v-model="emails"
        :aria-label="t('emails_label')"
        :input-test-id="TestId.directoryPdf.emails"
        autocomplete="email"
        inputmode="email"
        :disabled="sending"
      ></sp-text-field>
      <span class="export-email-form__hint">{{ t("emails_hint") }}</span>
      <span v-if="emailFieldErrors.emails" role="alert" class="export-email-form__error">{{
        emailFieldErrors.emails
      }}</span>
    </label>

    <div class="export-email-form__consent">
      <sp-checkbox
        v-model="emailConsent"
        :label="t('email_consent_label')"
        :disabled="sending"
        :data-testid="TestId.directoryPdf.emailConsent"
      ></sp-checkbox>
      <span v-if="emailFieldErrors.consent" role="alert" class="export-email-form__error">{{
        emailFieldErrors.consent
      }}</span>
    </div>

    <div class="export-email-form__actions">
      <sp-button
        color="primary"
        :disabled="!canSubmit"
        :data-testid="TestId.directoryPdf.emailSubmit"
        @click="emailPdf"
      >
        {{ submitLabel }}
      </sp-button>

      <!-- Always-rendered live region so the send outcome is announced. -->
      <span class="export-email-form__outcome" aria-live="polite">
        <Transition name="outcome-pop">
          <span
            v-if="outcome"
            :key="outcome.key"
            class="export-email-form__outcome-content"
            :class="`--${outcome.key}`"
            :data-testid="outcome.testId"
          >
            <sp-icon :name="outcome.icon" size="small"></sp-icon>
            {{ outcome.label }}
          </span>
        </Transition>
      </span>
    </div>
    <p v-if="rateLimited" role="alert" class="export-email-form__limit-notice">
      {{ t("email_rate_limited_error") }}
    </p>

    <p class="export-email-form__footnote">{{ t("email_footnote") }}</p>
  </section>
</template>

<script setup>
import { computed } from "vue";
import { TestId } from "#/test-ids";
import { t } from "#ce/apps/SpDirectoryPdfExport/i18n";
import { EMAIL_STATUS, useDirectoryPdfExportStore } from "#ce/apps/SpDirectoryPdfExport/store/export";

const store = useDirectoryPdfExportStore();
const { preparerName, emails, emailConsent, emailStatus, emailFieldErrors, emailRequestError, canEmail, emailPdf } =
  store;

const sending = computed(() => emailStatus.value === EMAIL_STATUS.SENDING);
const sent = computed(() => emailStatus.value === EMAIL_STATUS.SENT);
const rateLimited = computed(() => emailRequestError.value === "rate_limited");
const canSubmit = computed(() => canEmail.value && emailStatus.value === EMAIL_STATUS.IDLE);
const submitLabel = computed(() => (sending.value ? t("email_sending_label") : t("email_submit_label")));

const outcome = computed(() => {
  if (sent.value) {
    return {
      key: "sent",
      icon: "check-color",
      label: t("email_sent_label"),
      testId: TestId.directoryPdf.emailSent,
    };
  }
  if (rateLimited.value) {
    return {
      key: "rate-limited",
      icon: "info-filled",
      label: t("email_not_sent_label"),
      testId: TestId.directoryPdf.emailNotSent,
    };
  }
  if (emailRequestError.value) {
    return {
      key: "failed",
      icon: "important-filled",
      label: t("email_failed_label"),
      testId: TestId.directoryPdf.emailFailed,
    };
  }
  return null;
});
</script>

<style lang="scss" scoped>
.export-email-form {
  --warning-text-color: var(--sp-comp-directory-pdf-warning-text-color, var(--sp-sys-color-warning));
  --error-text-color: var(--sp-comp-directory-pdf-error-text-color, var(--sp-sys-color-error));
  --success-text-color: var(--sp-comp-directory-pdf-success-text-color, var(--sp-sys-color-success));

  display: grid;
  gap: var(--sp-ref-scale-20);
  padding: var(--sp-ref-scale-16);
  border: 1px solid var(--sp-sys-color-outline-variant);
  border-radius: var(--sp-ref-border-radius-sm);
  color: var(--sp-sys-color-on-surface);
  text-align: start;

  &__header {
    display: grid;
    gap: var(--sp-ref-scale-4);
  }

  &__title {
    margin: 0;
    font-family: var(--sp-sys-font-family-semi-bold);
    font-weight: var(--sp-sys-font-weight-semi-bold);
    font-size: var(--sp-ref-font-size-large);
    line-height: var(--sp-ref-line-height-medium);
  }

  &__intro {
    margin: 0;
  }

  &__field {
    display: grid;
    gap: var(--sp-ref-scale-8);
  }

  &__label {
    font-weight: var(--sp-sys-font-weight-semi-bold);
    font-family: var(--sp-sys-font-family-semi-bold);
  }

  &__hint {
    font-size: var(--sp-ref-font-size-small);
    line-height: var(--sp-sys-body-line-height);
    color: var(--sp-sys-color-on-surface-variant);
  }

  &__error {
    font-size: var(--sp-ref-font-size-small);
    color: var(--error-text-color);
  }

  &__consent {
    display: grid;
    gap: var(--sp-ref-scale-4);
  }

  &__actions {
    display: flex;
    align-items: center;
    gap: var(--sp-ref-scale-8);
  }

  &__outcome {
    display: flex;
    align-items: center;
  }

  &__outcome-content {
    --sp-comp-icon-fill: currentColor;
    display: inline-flex;
    align-items: center;
    gap: var(--sp-ref-scale-4);
    font-weight: var(--sp-sys-font-weight-bold);

    &.--sent {
      color: var(--success-text-color);
    }

    &.--failed {
      color: var(--error-text-color);
    }

    &.--rate-limited {
      color: var(--warning-text-color);
    }
  }

  &__limit-notice {
    margin: 0;
    color: var(--warning-text-color);
  }

  &__footnote {
    margin: 0;
    font-size: var(--sp-ref-font-size-x-small);
    line-height: var(--sp-ref-line-height-small);
    color: var(--sp-sys-color-on-surface-variant);
  }
}

.outcome-pop-enter-active {
  transition:
    opacity var(--sp-ref-transition-expand-duration) ease,
    transform var(--sp-ref-transition-expand-duration) ease;
}

.outcome-pop-enter-from {
  opacity: 0;
  transform: translateX(calc(var(--sp-ref-scale-8) * -1));
}

@media (prefers-reduced-motion: reduce) {
  .outcome-pop-enter-active {
    transition: none;
  }
}
</style>
